// src/pages/ExamSessions/ExamSessionList.jsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useExamSessions } from '../../hooks/useExamSessions';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Input from '../../components/common/Input';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const ExamSessionList = () => {
  const { examSessions, loading, error, deleteExamSession } = useExamSessions();
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = examSessions.filter((s) => {
    const q = searchTerm.toLowerCase();
    return (
      s.name?.toLowerCase().includes(q) ||
      s.notes?.toLowerCase().includes(q) ||
      (s.start_date && s.start_date.toLowerCase().includes(q)) ||
      (s.end_date && s.end_date.toLowerCase().includes(q))
    );
  });

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this exam session?')) {
      const result = await deleteExamSession(id);
      if (result.success) {
        alert('Exam session deleted successfully');
      } else {
        alert(result.error || 'Failed to delete exam session');
      }
    }
  };

  const columns = [
    {
      header: 'Session Name',
      accessor: (row) => row.name,
      cell: (value) => value || '-',
    },
    {
      header: 'Start Date',
      accessor: (row) => row.start_date,
      cell: (value) => value || '-',
    },
    {
      header: 'End Date',
      accessor: (row) => row.end_date,
      cell: (value) => value || '-',
    },
    {
      header: 'Notes',
      accessor: (row) => row.notes,
      cell: (value) => value || '-',
    },
    {
      header: 'Actions',
      accessor: (row) => row.id,
      cell: (value) => (
        <div className="flex space-x-2">
          <Link to={`/exam-sessions/${value}/edit`}>
            <Button variant="outline" size="sm">
              Edit
            </Button>
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDelete(value)}
            className="text-red-600 hover:text-red-800"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold text-gray-900">Exam Sessions Management</h1>
        <Link to="/exam-sessions/new">
          <Button>
            <PlusIcon className="h-5 w-5 mr-1" />
            Add Exam Session
          </Button>
        </Link>
      </div>

      <Card>
        <div className="mb-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              placeholder="Search exam sessions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading exam sessions...</div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">Error: {error}</div>
        ) : (
          <Table
            columns={columns}
            data={filtered}
            emptyMessage="No exam sessions found"
          />
        )}
      </Card>
    </div>
  );
};

export default ExamSessionList;
